import { analysisWaitExceeded } from '@yeshuman/sdk'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

export const FIT_RETRY_PENDING_QUERY_KEY = ['fit-analysis-retry-pending'] as const

export type FitRetryPendingMap = {
  opportunities: Record<string, number>
  applications: Record<string, number>
}

const EMPTY_PENDING: FitRetryPendingMap = { opportunities: {}, applications: {} }

function withoutKey(record: Record<string, number>, id: string): Record<string, number> {
  if (!(id in record)) return record
  const next = { ...record }
  delete next[id]
  return next
}

export function useFitRetryPending() {
  const queryClient = useQueryClient()
  const { data = EMPTY_PENDING } = useQuery({
    queryKey: FIT_RETRY_PENDING_QUERY_KEY,
    queryFn: () =>
      queryClient.getQueryData<FitRetryPendingMap>(FIT_RETRY_PENDING_QUERY_KEY) ?? EMPTY_PENDING,
    staleTime: Infinity,
    gcTime: Infinity,
    initialData: EMPTY_PENDING,
  })

  const [now, setNow] = useState(() => Date.now())
  const hasAny =
    Object.keys(data.opportunities).length > 0 || Object.keys(data.applications).length > 0

  useEffect(() => {
    if (!hasAny) return
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [hasAny])

  const write = useCallback(
    (updater: (prev: FitRetryPendingMap) => FitRetryPendingMap) => {
      queryClient.setQueryData<FitRetryPendingMap>(FIT_RETRY_PENDING_QUERY_KEY, (prev) =>
        updater(prev ?? EMPTY_PENDING),
      )
    },
    [queryClient],
  )

  const markOpportunityPending = useCallback(
    (opportunityId: string) => {
      write((prev) => ({
        ...prev,
        opportunities: { ...prev.opportunities, [opportunityId]: Date.now() },
      }))
    },
    [write],
  )

  const ensureOpportunityPending = useCallback(
    (opportunityId: string) => {
      write((prev) => {
        if (prev.opportunities[opportunityId] != null) return prev
        return {
          ...prev,
          opportunities: { ...prev.opportunities, [opportunityId]: Date.now() },
        }
      })
    },
    [write],
  )

  const markApplicationPending = useCallback(
    (applicationId: string) => {
      write((prev) => ({
        ...prev,
        applications: { ...prev.applications, [applicationId]: Date.now() },
      }))
    },
    [write],
  )

  const clearOpportunityPending = useCallback(
    (opportunityId: string) => {
      write((prev) => {
        const opportunities = withoutKey(prev.opportunities, opportunityId)
        if (opportunities === prev.opportunities) return prev
        return { ...prev, opportunities }
      })
    },
    [write],
  )

  const clearApplicationPending = useCallback(
    (applicationId: string) => {
      write((prev) => {
        const applications = withoutKey(prev.applications, applicationId)
        if (applications === prev.applications) return prev
        return { ...prev, applications }
      })
    },
    [write],
  )

  const waitingOpportunityIds = useMemo(
    () => new Set(Object.keys(data.opportunities)),
    [data.opportunities],
  )
  const waitingApplicationIds = useMemo(
    () => new Set(Object.keys(data.applications)),
    [data.applications],
  )

  const stuckOpportunityIds = useMemo(() => {
    const stuck = new Set<string>()
    for (const [id, startedAt] of Object.entries(data.opportunities)) {
      if (analysisWaitExceeded(startedAt, now)) stuck.add(id)
    }
    return stuck
  }, [data.opportunities, now])

  const stuckApplicationIds = useMemo(() => {
    const stuck = new Set<string>()
    for (const [id, startedAt] of Object.entries(data.applications)) {
      if (analysisWaitExceeded(startedAt, now)) stuck.add(id)
    }
    return stuck
  }, [data.applications, now])

  const pendingOpportunityIds = useMemo(() => {
    const pending = new Set(waitingOpportunityIds)
    for (const id of stuckOpportunityIds) pending.delete(id)
    return pending
  }, [stuckOpportunityIds, waitingOpportunityIds])

  const pendingApplicationIds = useMemo(() => {
    const pending = new Set(waitingApplicationIds)
    for (const id of stuckApplicationIds) pending.delete(id)
    return pending
  }, [stuckApplicationIds, waitingApplicationIds])

  return {
    opportunityStartedAt: data.opportunities,
    applicationStartedAt: data.applications,
    waitingOpportunityIds,
    waitingApplicationIds,
    pendingOpportunityIds,
    pendingApplicationIds,
    stuckOpportunityIds,
    stuckApplicationIds,
    markOpportunityPending,
    ensureOpportunityPending,
    markApplicationPending,
    clearOpportunityPending,
    clearApplicationPending,
  }
}
